/**
 * Поиск по номеру телефона или номеру сделки/лида: где номер есть (Bitrix,
 * CRM, салон, договор) и какой объект сейчас в работе. Спека
 * `docs/specs/2026-09-25-phone-search-design.md`; те же данные —
 * `prokat-runner phone-search Q`.
 */
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import { loadSearch, type SearchResult, VIA, type Via } from "@/lib/search";

function day(v: string | null): string {
  return v ? new Date(v).toLocaleDateString("ru-RU") : "—";
}

function Badges({ via }: { via: Via[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {via.map((v) => (
        <span key={v} title={VIA[v].hint} className="rounded-[var(--radius-ui)] border border-border px-1.5 text-[11.5px] text-fg-muted">
          {VIA[v].label}
        </span>
      ))}
    </span>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (!count) return null;
  return (
    <section className="flex flex-col gap-1.5" data-annot={title}>
      <h2 className="text-[13px] font-semibold">
        {title} <span className="text-fg-faint">{count}</span>
      </h2>
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full text-[12.5px]">
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

const TD = "px-2.5 py-1.5 align-top";
const dot = (active: boolean) => <span className={active ? "text-ok" : "text-fg-faint"}>{active ? "●" : "○"}</span>;

function Results({ r }: { r: SearchResult }) {
  const empty = !r.deals.length && !r.leads.length && !r.contacts.length && !r.customers.length && !r.contracts.length;
  return (
    <>
      {r.errors.length > 0 && (
        <div className="rounded-[var(--radius-card)] border border-warn p-2 text-[12.5px] text-warn">
          Поиск неполный: {r.errors.join("; ")}
        </div>
      )}
      <div className="text-[12px] text-fg-muted">Номера: {r.phones.join(", ") || "—"}</div>
      {r.main ? (
        <div className="rounded-[var(--radius-card)] border border-accent p-3 text-[13px]" data-annot="главный объект">
          <div className="font-semibold">
            {r.main.entity === "deal" ? (
              <Link className="underline" to={`/deals/${r.main.id}`}>Сделка {r.main.id}</Link>
            ) : (
              <Link className="underline" to={`/leads/${r.main.id}`}>Лид {r.main.id}</Link>
            )}
          </div>
          <div className="text-fg-muted">{r.main.reason}</div>
        </div>
      ) : (
        empty && <div className="text-[12.5px] text-fg-muted">Ничего не нашли ни в Bitrix, ни в CRM.</div>
      )}
      <Section title="Сделки" count={r.deals.length}>
        {r.deals.map((d) => (
          <tr key={d.id} className="border-b border-border last:border-0">
            <td className={TD}>{dot(d.active)} <Link className="underline" to={`/deals/${d.id}`}>{d.id}</Link></td>
            <td className={TD}>{d.title ?? "—"}</td>
            <td className={TD}>{d.stage_name ?? d.stage ?? "—"}</td>
            <td className={TD}>{d.category ?? "—"}</td>
            <td className={TD}>{day(d.created)}</td>
            <td className={TD}>{d.assigned ?? "—"}</td>
            <td className={TD}><Badges via={d.via} /></td>
          </tr>
        ))}
      </Section>
      <Section title="Лиды" count={r.leads.length}>
        {r.leads.map((l) => (
          <tr key={l.id} className="border-b border-border last:border-0">
            <td className={TD}>{dot(l.active)} <LeadLink id={l.id} /></td>
            <td className={TD}>{l.title ?? "—"}</td>
            <td className={TD}>{l.status_name}</td>
            <td className={TD}>{day(l.created)}</td>
            <td className={TD}>{l.assigned ?? "—"}</td>
            <td className={TD}><Badges via={l.via} /></td>
          </tr>
        ))}
      </Section>
      <Section title="Контакты Bitrix" count={r.contacts.length}>
        {r.contacts.map((c) => (
          <tr key={c.id} className="border-b border-border last:border-0">
            <td className={TD}>{c.id}</td>
            <td className={TD}>{c.name || "—"}</td>
            <td className={TD}>{c.phones.join(", ")}</td>
            <td className={TD}><Badges via={c.via} /></td>
          </tr>
        ))}
      </Section>
      <Section title="Клиенты салона" count={r.customers.length}>
        {r.customers.map((c) => (
          <tr key={c.id} className="border-b border-border last:border-0">
            <td className={TD}>{c.id}</td>
            <td className={TD}>{c.name ?? "—"}</td>
            <td className={TD}>{c.status ?? "—"}</td>
            <td className={TD}>{day(c.date_start)}</td>
            <td className={TD}>
              {c.deal_ids.map((d) => <Link key={d} className="mr-1.5 underline" to={`/deals/${d}`}>{d}</Link>)}
            </td>
            <td className={TD}><Badges via={c.via} /></td>
          </tr>
        ))}
      </Section>
      <Section title="Договоры" count={r.contracts.length}>
        {r.contracts.map((c) => (
          <tr key={c.id} className="border-b border-border last:border-0">
            <td className={TD}>{c.id}</td>
            <td className={TD}>{c.deal_id ? <Link className="underline" to={`/deals/${c.deal_id}`}>сделка {c.deal_id}</Link> : "—"}</td>
            <td className={TD}>{c.status ?? "—"}</td>
            <td className={TD}><Badges via={c.via} /></td>
          </tr>
        ))}
      </Section>
    </>
  );
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const [draft, setDraft] = useState(q);
  // ?q= сменился ссылкой при открытой странице — поле за ним.
  useEffect(() => setDraft(q), [q]);
  const res = useQuery({ queryKey: ["search", q], queryFn: () => loadSearch(q), enabled: q.length > 0 });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams(draft.trim() ? { q: draft.trim() } : {});
  };
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[15px] font-semibold">Поиск</h1>
        <span className="text-[12px] text-fg-faint">по номеру телефона или номеру сделки/лида</span>
      </div>
      <form onSubmit={submit} className="flex gap-2" data-annot="поле поиска">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="+7 900 000-00-00 или 1252752"
          aria-label="Номер телефона или сделки"
          className="h-8 w-72 rounded-[var(--radius-ui)] border border-border bg-transparent px-2.5 text-[13px]"
        />
        <button type="submit" className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover">
          Найти
        </button>
      </form>
      {!q ? (
        <div className="text-[12.5px] text-fg-muted">Введите номер — покажу сделки, лиды, контакты, клиентов салона и договоры.</div>
      ) : res.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Ищу в Bitrix и CRM…</div>
      ) : res.isError ? (
        <div className="text-[12.5px] text-err">Не удалось найти: {String(res.error.message)}</div>
      ) : (
        <Results r={res.data} />
      )}
    </main>
  );
}
